/**
 * Notice terms in a contract: deadlines, how notice must be delivered, and where it goes. They are
 * the customer's own contract terms, quoted and confirmed by the customer, and shown beside PayRight's
 * rule-based dates. PayRight does not calculate a date from them, and they are not legal advice.
 */
export type NoticeTerm = {
  kind: 'DEADLINE' | 'DELIVERY' | 'ADDRESS'; summary: string; quote: string;
  days?: number; dayType?: 'CALENDAR' | 'BUSINESS'; basis?: 'FIRST_WORK' | 'LAST_WORK' | 'COMPLETION' | 'EVENT' | 'OTHER';
};

const WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  fourteen: 14, fifteen: 15, twenty: 20, 'twenty-one': 21, thirty: 30, 'forty-five': 45, sixty: 60, ninety: 90 };
const tidy = (text: string) => text.replace(/\s+/g, ' ').trim();
const clip = (text: string, length: number) => text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text;

const BASIS_PHRASE = { FIRST_WORK: 'of starting the work', LAST_WORK: 'of the last day of work', COMPLETION: 'of completion', EVENT: 'of the event the notice is about', OTHER: '' } as const;
function basisOf(after: string): NonNullable<NoticeTerm['basis']> {
  const near = after.slice(0, 90);
  if (/\b(?:commenc|start|begin|mobiliz|first)\w*/i.test(near)) return 'FIRST_WORK';
  if (/\blast\s+(?:day|date)\b/i.test(near)) return 'LAST_WORK';
  if (/\b(?:completion|complet|final|terminat)\w*/i.test(near)) return 'COMPLETION';
  if (/\b(?:receipt|occurrence|discover|becom|delay|default|change|event|knowledge|aware|request)\w*/i.test(near)) return 'EVENT';
  return 'OTHER';
}

export function noticeTermsFromContract(rawText: string): NoticeTerm[] {
  const sentences = rawText.replace(/\r\n?/g, '\n').split(/\n\s*\n/).flatMap(paragraph => tidy(paragraph).split(/(?<=[.;:])\s+(?=[A-Z(0-9])/));
  const terms: NoticeTerm[] = [];
  const add = (term: NoticeTerm) => { if (terms.length < 12 && !terms.some(known => known.summary === term.summary)) terms.push(term); };
  for (const sentence of sentences) {
    if (!/\bnotice[sd]?\b|\bnotif(?:y|ication)\b/i.test(sentence) || /\bnotices?\s+of\s+commencement\b/i.test(sentence)) continue;
    const quote = clip(sentence, 400);
    const within = /\bwithin\s+(?:(\d{1,3})|([a-z]+(?:-[a-z]+)?))(?:\s*\(\s*(\d{1,3})\s*\))?\s+(calendar\s+|business\s+|working\s+|consecutive\s+)?days?\b/i.exec(sentence);
    const days = within ? Number(within[1] ?? within[3] ?? WORDS[within[2]!.toLowerCase()]) : NaN;
    if (within && Number.isInteger(days) && days >= 1 && days <= 365) {
      const basis = basisOf(sentence.slice(within.index + within[0].length));
      const dayType = /business|working/i.test(within[4] ?? '') ? 'BUSINESS' : /calendar|consecutive/i.test(within[4] ?? '') ? 'CALENDAR' : undefined;
      add({ kind: 'DEADLINE', summary: tidy(`Notice within ${days}${dayType ? ` ${dayType.toLowerCase()}` : ''} days ${BASIS_PHRASE[basis]}`), quote, days, basis, ...(dayType ? { dayType } : {}) });
    }
    const method = /\b(certified\s+mail|registered\s+mail|hand[- ]deliver\w*|personal(?:ly)?\s+deliver\w*|courier|overnight|e-?mail|facsimile|fax)\b/i.exec(sentence);
    if (method && /\b(?:shall|must|required|only|will)\b/i.test(sentence)) add({ kind: 'DELIVERY', summary: `Notice by ${tidy(method[1]!.toLowerCase())}`, quote });
    if (/\bnotices?\b[^.]{0,80}\b(?:sent|addressed|directed|delivered|mailed)\s+to\b|\battention:/i.test(sentence)) add({ kind: 'ADDRESS', summary: 'Notices go to the address named in the contract', quote });
  }
  return terms;
}

const day = (date: string) => Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10));
/** The day a "within N days of starting work" term lands on, when the first day of work is known. Business-day terms are not computed. */
export function contractDeadline(term: NoticeTerm, firstFurnishingDate?: string | null): string | null {
  if (term.kind !== 'DEADLINE' || term.basis !== 'FIRST_WORK' || !term.days || term.dayType === 'BUSINESS' || !firstFurnishingDate) return null;
  // Calendar arithmetic on a date with no time of day, so no time zone can move it.
  const result = new Date(day(firstFurnishingDate) + term.days * 86400000);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${result.getUTCFullYear()}-${pad(result.getUTCMonth() + 1)}-${pad(result.getUTCDate())}`;
}
export function compareDates(contractDate: string, statutoryDate: string): 'EARLIER' | 'LATER' | 'SAME' {
  const difference = day(contractDate) - day(statutoryDate);
  return difference < 0 ? 'EARLIER' : difference > 0 ? 'LATER' : 'SAME';
}
